import { Badge } from '@/shared/ui/Badge';
import { Callout } from '@/shared/ui/Callout';
import { formatDate } from '@/shared/utils/format';

import type { Email, EmailDetail } from './types';

/** «2 oct 2026, 18:30», like the line under the subject. */
const whenFormat = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

type Located = Pick<Email, 'location_unknown_at' | 'trashed_at' | 'reviewed'>;

/**
 * Its owner took it out of the inbox from their mail client and nobody answered it here
 * (contract v10.34, ADR 0029 of the back): it left «Por revisar» for that, and «Eliminar»
 * in the platform is the trash instead.
 */
export function leftUnanswered(email: Located): boolean {
  return Boolean(email.location_unknown_at) && !email.trashed_at && !email.reviewed;
}

/** The row's badge in «Todos» and the folder lists, by exception like the others. */
export function OutOfInboxBadge({ email }: { email: Located }) {
  if (!leftUnanswered(email)) return null;
  return (
    <Badge
      tone="neutral"
      title="Lo movieron a otra carpeta o lo borraron en el programa de correo sin responder aquí. Por eso no sale en «Por revisar»."
    >
      Fuera de la bandeja
    </Badge>
  );
}

/** «a x», «a x y a y», «a x, a y y a z». */
function addressList(to: readonly string[]): string {
  return to.length > 1 ? `a ${to.slice(0, -1).join(', a ')} y a ${to.at(-1)}` : `a ${to[0]}`;
}

/**
 * The detail's notice: the original is no longer in the inbox of the owner's mail client,
 * since when, and, before anyone answers, that «Correcto» still forwards. Victor may have
 * forwarded it from Thunderbird already, and a second copy is the one thing this screen
 * can still prevent.
 */
export function OutOfInboxNotice({ email }: { email: EmailDetail }) {
  if (!email.location_unknown_at || email.trashed_at) return null;
  const forwardsTo = email.reviewed ? [] : (email.triage_plan?.will_forward_to ?? []);
  return (
    <Callout
      tone={forwardsTo.length > 0 ? 'warn' : 'neutral'}
      compact
      role="status"
      title="Ya no está en la bandeja de entrada de tu correo."
    >
      Desde el {formatDate(whenFormat, email.location_unknown_at)}: lo movieron a otra carpeta o lo
      borraron en el programa de correo (Thunderbird, el webmail).
      {email.reviewed ? null : ' Por eso ya no sale en «Por revisar».'}
      {forwardsTo.length > 0 ? (
        <strong className="mt-1 block font-medium">
          «Correcto» lo reenvía igual {addressList(forwardsTo)}: si ya lo reenviaste desde tu
          correo, llegará dos veces.
        </strong>
      ) : null}
    </Callout>
  );
}
